import { DY_DELIVERY_DATES_DISABLED } from '../index'

export interface ParseDateOptions {
  dateString: string | Date
  includeHour?: boolean
  includeSeconds?: boolean
  includeTimezone?: boolean
  locale?:
    | 'es-ES'
    | 'ca-CA'
    | 'en-EU'
    | 'es-ES'
    | 'es-CN'
    | 'es-CT'
    | 'es-MD'
    | 'en-GB'
    | 'fr-FR'
    | 'de-DE'
    | 'pt-PT'
  options?: Intl.DateTimeFormatOptions
}

const isValidDate = (dateString) => !isNaN(Date.parse(dateString))

export function parseISODateSafe(dateString: string | Date): Date {
  if (dateString instanceof Date) return dateString

  if (!dateString || typeof dateString !== 'string') {
    console.warn('🗓️ Invalid date input:', dateString)
    return new Date()
  }

  const isoDateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/
  if (isoDateOnlyPattern.test(dateString)) {
    const [year, month, day] = dateString.split('-').map(Number)
    return new Date(year, month - 1, day, 0, 0, 0, 0)
  }

  const isoWithTimePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/
  if (isoWithTimePattern.test(dateString)) {
    const dateOnly = dateString.split('T')[0]
    const [year, month, day] = dateOnly.split('-').map(Number)
    return new Date(year, month - 1, day, 0, 0, 0, 0)
  }

  console.warn('🗓️ Unexpected date format, falling back to default parsing:', dateString)
  return new Date(dateString)
}

export function parseDateString({
  dateString,
  locale = 'es-ES',
  includeHour = false,
  includeSeconds = false,
  includeTimezone = false,
  options = { year: 'numeric', month: 'short', day: 'numeric' },
}: ParseDateOptions): string {
  if (!isValidDate(dateString)) {
    console.warn(
      '🗓️ dateString passed is not a valid date pattern and cannot be parsed'
    )
    return ''
  }

  const date = new Date(dateString)

  if (includeHour) {
    options.hour = '2-digit'
    options.minute = '2-digit'
    if (includeSeconds) options.second = '2-digit'
  }

  if (includeTimezone) options.timeZoneName = 'short'

  return date.toLocaleString(locale, options)
}

export function calculateDaysUntilExpiration(now, createdDate) {
  // Calculate the time difference in milliseconds
  const timeDifference = Math.abs(Date.parse(now) - Date.parse(createdDate))

  // Convert milliseconds to days
  const daysDifference = Math.ceil(timeDifference / (1000 * 60 * 60 * 24))

  return daysDifference
}

export function getDayOfWeekName(lang: string, dayOfWeek: 0 | 1 | 2 | 3 | 4 | 5 | 6): string {
  if (typeof dayOfWeek !== 'number' || dayOfWeek < 0 || dayOfWeek > 6) {
    throw new Error(`Invalid dayOfWeek: ${dayOfWeek}`)
  }

  const options: Intl.DateTimeFormatOptions = { weekday: 'long' }
  const formatter = new Intl.DateTimeFormat(lang, options)

  const referenceSunday = new Date(Date.UTC(2023, 0, 1)) // Jan 1, 2023 = Sunday
  const targetDate = new Date(referenceSunday)
  targetDate.setUTCDate(referenceSunday.getUTCDate() + dayOfWeek)

  return formatter.format(targetDate)
}

// export function getDayOfWeekName(lang: string, dayOfWeek: 0 | 1 | 2 | 3 | 4 | 5 | 6):string {
//   const options = { weekday: 'long', locale: lang } as Intl.DateTimeFormatOptions
//   const formatter = new Intl.DateTimeFormat(lang, options)

//   // Get the name of the week day based on the day of the week integer
//   const dayName = formatter.format(new Date(Date.UTC(new Date().getFullYear(), 0, dayOfWeek )))
//   return dayName
// }

export function getDateFormatted(locale: string, dateString: string | Date): string {
  const date = parseISODateSafe(dateString)

  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    day: 'numeric',
    month: 'numeric',
  }

  const formatter = new Intl.DateTimeFormat(locale, options)
  const parts = formatter.formatToParts(date)
  const formattedDate = `${parts[0].value} ${parts[2].value}/${parts[4].value}`

  return formattedDate
}

export function groupByDate(data = []) {
  if (!data.length) return []
	const groupedByDate = new Map()

	// In case delivery dates are disabled, create a single group with all the items keeping the original order
	if (DY_DELIVERY_DATES_DISABLED) {
		groupedByDate.set('all', data)
		return Array.from(groupedByDate.values())
	}

  const cartLines = [...data]
  cartLines.sort((a, b) => {
    const dateA = parseISODateSafe(a.variant.deliveryDate)
    const dateB = parseISODateSafe(b.variant.deliveryDate)
    return dateA.getTime() - dateB.getTime()
  })

  cartLines.forEach((item) => {
    const deliveryDate = parseISODateSafe(item.variant.deliveryDate)

    const key = new Date(
      deliveryDate.getFullYear(),
      deliveryDate.getMonth(),
      deliveryDate.getDate()
    ).toString()

    if (groupedByDate.has(key)) {
      groupedByDate.get(key)!.push(item)
    } else {
      groupedByDate.set(key, [item])
    }
  })

  const result = Array.from(groupedByDate.values())
  return result
}
